import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { useActiveTab } from '@/components/navigation/NavigationTabs/useActiveTab';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { COLORS } from '@/styles/tokens/colors';
import { RoleKind } from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';

import { processCapabilities } from './AdminRolesPage.utils';
import { CreateRoleModal } from './CreateRoleModal/CreateRoleModal';
import { useGetRolesQuery } from './graphql/GetRoles.generated';
import { RoleViewer } from './RoleViewer/RoleViewer';

export function AdminRolesPage() {
  const { data, loading, error } = useGetRolesQuery();
  const { activeTab: selectedRoleId, setActiveTab: setSelectedRoleId } =
    useActiveTab<string>({
      defaultTab: '',
      filterSearchParam: 'role',
    });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const roles = getNodes(data?.roles).sort((a, b) => {
    // Sort Administrator roles to the top
    if (
      a.kind === RoleKind.Administrator &&
      b.kind !== RoleKind.Administrator
    ) {
      return -1;
    }
    if (
      b.kind === RoleKind.Administrator &&
      a.kind !== RoleKind.Administrator
    ) {
      return 1;
    }
    // Otherwise maintain original order
    return 0;
  });
  const selectedRole = roles.find((role) => role.id === selectedRoleId);

  // Use the comprehensive set of capabilities from the GetRoles query
  const allCapabilities = (data?.capabilities || []).map((cap) => ({
    id: cap.id,
    authzedRelationName: cap.authzedRelationName,
  }));

  if (loading) {
    return (
      <SubpageLayout>
        <Typography variant="body-xs">Loading...</Typography>
      </SubpageLayout>
    );
  }

  if (error) {
    return (
      <SubpageLayout>
        <Typography variant="body-xs" color="error">
          Error: {error.message}
        </Typography>
      </SubpageLayout>
    );
  }

  const handleAddRoleClick = () => {
    setIsCreateModalOpen(true);
  };

  return (
    <SubpageLayout
      heading="Roles"
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={handleAddRoleClick}
        >
          Add role
        </Button>
      }
    >
      <Stack direction="row" spacing={2} sx={{ height: '80vh' }}>
        {/* Roles List - 50% width */}
        <Box sx={{ width: '50%', overflowY: 'auto' }}>
          <Stack spacing={1}>
            {roles.map((role) => (
              <Card
                variant="outlined"
                key={role.id}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border:
                    selectedRoleId === role.id
                      ? `1px solid ${COLORS.BLUE[500]}`
                      : undefined,
                  '&:hover': {
                    backgroundColor: COLORS.GRAY[50],
                  },
                }}
                onClick={() => setSelectedRoleId(role.id)}
              >
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="title-s" component="h3">
                      {role.displayName}
                    </Typography>
                    {role.isDefault && (
                      <Badge
                        variant={BadgeVariants.SecondaryFilled}
                        display="Default"
                      />
                    )}
                    {/* Don't allow editing system roles */}
                    {role.kind !== RoleKind.Custom && (
                      <Typography variant="body-xs">(Read-only)</Typography>
                    )}
                  </Stack>
                  <Typography variant="body-xs">
                    {pluralize({
                      count: role.employees?.totalCount || 0,
                      singular: 'user',
                    })}
                  </Typography>
                </Stack>

                <Box>
                  {processCapabilities(role.capabilities).map((capability) => (
                    <Box
                      sx={{ display: 'inline-block', mb: 0.5 }}
                      component="span"
                      key={capability.id}
                    >
                      <Badge
                        variant={capability.variant}
                        display={capability.displayName}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* Role Viewer - 50% width */}
        <Box sx={{ width: '50%' }}>
          <RoleViewer
            role={selectedRole}
            allCapabilities={allCapabilities}
            onRoleDeleted={() => {
              // Clear selection when role is deleted
              setSelectedRoleId('');
            }}
          />
        </Box>
      </Stack>

      {/* Create Role Modal */}
      {isCreateModalOpen && (
        <CreateRoleModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          allCapabilities={allCapabilities}
        />
      )}
    </SubpageLayout>
  );
}
